import React from 'react'
import { ChevronDown } from 'react-feather'

interface Permission {
  id: string
  label: string
  isSelected: boolean
  weight: number
  visible: boolean
  parent_permission_id: string | null
  rolesAllowed: Permission[]
}

interface PermissionAccordionProps {
  permission: Permission
  handlePermToggle: (event: React.ChangeEvent<HTMLInputElement>, id: string) => void
  isChild?: boolean
  expandAll?: boolean
}

const PermissionAccordion: React.FC<PermissionAccordionProps> = ({
  permission,
  handlePermToggle,
  isChild,
  expandAll
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const checkboxRef = React.useRef<HTMLInputElement>(null)

  const totalRoles = permission.rolesAllowed?.length || 0
  const selectedRoles = permission.rolesAllowed?.filter(role => role.isSelected).length || 0

  // Update indeterminate state for the checkbox
  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = totalRoles > 0 && selectedRoles > 0 && selectedRoles < totalRoles
    }
  }, [selectedRoles, totalRoles])

  // Update local isExpanded state based on expandAll prop
  React.useEffect(() => {
    if (expandAll !== undefined) {
      setIsExpanded(expandAll)
    }
  }, [expandAll])

  const toggleAccordion = () => {
    if (totalRoles > 0) {
      setIsExpanded(prev => !prev)
    }
  }

  return (
    <div className='rounded-md'>
      <div className={`flex items-center px-4 ${isChild ? 'mb-2' : 'py-4'} cursor-pointer`} onClick={toggleAccordion}>
        <input
          id={`checkbox-${permission.id}`}
          type='checkbox'
          ref={checkboxRef}
          checked={permission.isSelected}
          onChange={event => handlePermToggle(event, permission.id)}
          className='mr-3 w-4 h-4 p-1 accent-[#4D4D4D]'
        />
        <label htmlFor={`checkbox-${permission.id}`} className='mr-auto text-base font-normal cursor-pointer'>
          {permission.label}
        </label>
        {totalRoles > 0 && (
          <span className='text-sm font-semibold text-gray-500'>
            {selectedRoles}/{totalRoles}
          </span>
        )}
        {totalRoles > 0 && (
          <span
            className={`ml-4 transition-transform transform duration-300 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
          >
            <ChevronDown />
          </span>
        )}
      </div>
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'opacity-100 translate-y-0 max-h-screen' : 'opacity-0 -translate-y-4 max-h-0'
        }`}
      >
        <div className={`ml-6 ${isChild ? '' : 'mb-4'}`}>
          {permission.rolesAllowed
            .filter(role => !role.rolesAllowed?.length)
            .map(role => (
              <div className='flex items-center ml-4 mb-2' key={`${permission.id}-${role.id}`}>
                <input
                  id={`checkbox-${role.id}`}
                  type='checkbox'
                  checked={role.isSelected}
                  onChange={event => handlePermToggle(event, role.id)}
                  className='mr-3 w-4 h-4 p-1 accent-[#4D4D4D]'
                />
                <label htmlFor={`checkbox-${role.id}`} className='text-base font-normal cursor-pointer'>
                  {role.label}
                </label>
              </div>
            ))}
          {permission.rolesAllowed
            .filter(item => item.rolesAllowed?.length > 0)
            .map(childPermission => (
              <PermissionAccordion
                key={childPermission.id}
                permission={childPermission}
                handlePermToggle={handlePermToggle}
                isChild={true}
                expandAll={isExpanded} // Propagate expanded state
              />
            ))}
        </div>
      </div>
    </div>
  )
}

export default PermissionAccordion
